import { useFrame } from '@react-three/fiber';
// packages/character-render/src/HumanoidMesh.tsx
//
// Renders a humanoid character as bevelled voxel boxes. Voxels are batched by
// surface (roughness/metalness/emissive) into one InstancedMesh each, with the
// colour per instance, so a figure costs 1–3 draw calls whatever its palette.
// Every part honours its `scale`, `offset` and `rotation`.
//
// Optional procedural animation (`idle`, `walk`, `attack`, `death`) rewrites
// the instance matrices in useFrame from a rigid bone rig (see humanoidRig.ts):
// no React state, no per-frame allocation.
//
// Pure presentation: takes parts as props, no global store/context.
import { type MutableRefObject, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { getHumanoidBoxGeometry, getHumanoidMaterial } from './humanoidMaterials';
import {
  HUMANOID_BONES,
  type HumanoidAnimation,
  type HumanoidPart,
  type HumanoidRig,
  buildHumanoidRig,
  computeBoneMatrices,
  emptyPose,
} from './humanoidRig';

/** A light-bearing attach point on the figure — e.g. a carried lantern. */
export interface LanternSlot {
  /** Position in the character's local space. Default shoulder height [0, 2.4, 0]. */
  offset?: [number, number, number];
  /** Emissive + light color. Default warm amber. */
  color?: string;
  /** Point-light intensity. Default 3. */
  intensity?: number;
  /** Point-light reach in world units. Default 10. */
  distance?: number;
  /** Emit actual light (the mesh always renders). Default true. */
  light?: boolean;
}

export interface HumanoidMeshProps {
  parts: readonly HumanoidPart[];
  /** Optional uniform scale applied to the whole figure. Default 1. */
  scale?: number;
  /** Optional cast shadows. Default true. */
  castShadow?: boolean;
  /** Optional lantern attach point. Absent when unset. */
  lantern?: LanternSlot;
  /**
   * Procedural animation. Default 'none' (static rest pose, zero per-frame
   * cost). Any other value mounts a useFrame driver, so it must render inside
   * an R3F Canvas.
   */
  animation?: HumanoidAnimation;
  /** Animation speed multiplier. Default 1. */
  animationSpeed?: number;
  /** Phase offset (radians/seconds) so a crowd does not breathe in lockstep. Default 0. */
  animationPhase?: number;
  /** Bevelled (rounded-edge) boxes. Default true. */
  bevel?: boolean;
}

const LANTERN_DEFAULT_OFFSET: [number, number, number] = [0, 2.4, 0];
const LANTERN_DEFAULT_COLOR = '#ffaa44';

type MeshRefs = MutableRefObject<Array<THREE.InstancedMesh | null>>;

function isInstanced(mesh: unknown): mesh is THREE.InstancedMesh {
  return !!mesh && typeof (mesh as THREE.InstancedMesh).setMatrixAt === 'function';
}

export function HumanoidMesh({
  parts,
  scale = 1,
  castShadow = true,
  lantern,
  animation = 'none',
  animationSpeed = 1,
  animationPhase = 0,
  bevel = true,
}: HumanoidMeshProps) {
  const rig = useMemo(() => buildHumanoidRig(parts), [parts]);
  const meshRefs: MeshRefs = useRef([]);
  const geometry = getHumanoidBoxGeometry(bevel);

  // Rest pose + colours. Layout effect so the first rendered frame is correct
  // (instanceColor must exist before the program compiles).
  useLayoutEffect(() => {
    rig.batches.forEach((batch, b) => {
      const mesh = meshRefs.current[b];
      if (!isInstanced(mesh)) return;
      for (let i = 0; i < batch.count; i++) {
        mesh.setMatrixAt(i, batch.rest[i]!);
        mesh.setColorAt(i, batch.colors[i]!);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();
      // Poses swing limbs past the rest bounds; pad so culling never clips them.
      if (mesh.boundingSphere) mesh.boundingSphere.radius += rig.height * 0.35;
    });
  }, [rig]);

  return (
    <group scale={scale}>
      {rig.batches.map((batch, b) => (
        <instancedMesh
          key={batch.key}
          ref={(m: THREE.InstancedMesh | null) => {
            meshRefs.current[b] = m;
          }}
          args={[geometry, getHumanoidMaterial(batch.surface), batch.count]}
          castShadow={castShadow}
          receiveShadow
          dispose={null}
        />
      ))}
      {animation !== 'none' && (
        <HumanoidAnimator
          rig={rig}
          meshRefs={meshRefs}
          animation={animation}
          speed={animationSpeed}
          phase={animationPhase}
        />
      )}
      {lantern && (
        <group data-testid="lantern-attach" position={lantern.offset ?? LANTERN_DEFAULT_OFFSET}>
          <mesh>
            <boxGeometry args={[0.3, 0.3, 0.3]} />
            <meshStandardMaterial
              color="#222222"
              emissive={lantern.color ?? LANTERN_DEFAULT_COLOR}
              emissiveIntensity={1}
            />
          </mesh>
          {lantern.light !== false && (
            <pointLight
              color={lantern.color ?? LANTERN_DEFAULT_COLOR}
              intensity={lantern.intensity ?? 3}
              distance={lantern.distance ?? 10}
            />
          )}
        </group>
      )}
    </group>
  );
}

interface HumanoidAnimatorProps {
  rig: HumanoidRig;
  meshRefs: MeshRefs;
  animation: HumanoidAnimation;
  speed: number;
  phase: number;
}

/** Drives the instance matrices from the bone rig. Mounted only inside a Canvas. */
function HumanoidAnimator({ rig, meshRefs, animation, speed, phase }: HumanoidAnimatorProps) {
  const state = useMemo(
    () => ({
      t: 0,
      bones: HUMANOID_BONES.map(() => new THREE.Matrix4()),
      pose: emptyPose(),
      tmp: new THREE.Matrix4(),
    }),
    [],
  );

  // Death is a one-shot: restart its clock whenever the animation changes.
  useLayoutEffect(() => {
    state.t = 0;
  }, [animation, state]);

  useFrame((_, dt) => {
    state.t += Math.min(dt, 0.1) * speed;
    computeBoneMatrices(rig, animation, state.t, phase, state.pose, state.bones);
    for (let b = 0; b < rig.batches.length; b++) {
      const batch = rig.batches[b]!;
      const mesh = meshRefs.current[b];
      if (!isInstanced(mesh)) continue;
      for (let i = 0; i < batch.count; i++) {
        state.tmp.multiplyMatrices(state.bones[batch.bones[i]!]!, batch.rest[i]!);
        mesh.setMatrixAt(i, state.tmp);
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
  });

  return null;
}
